'use client';

import {
  type ClientApplicationConfigOptions,
  type ClientApplicationContext,
  getClientApplicationContext,
} from '@hc-frontend/core-context';
import { createDomEventBus } from '@hc-frontend/core-events';
import {
  getMemberIdClientSide,
  getMemberTokenClientSide,
  useAuthentication,
} from '@hc-frontend/features-authentication';
import { getClientLoggerFactory } from '@hc-frontend/features-logger';
import {
  createDatadogPublisherCount,
  trackCountMetricFactory,
} from '@hc-frontend/features-performance-monitoring';
import {
  CompleteTrackingProvider,
  getSessionIdClientSide,
  getUserIdClientSide,
  useCompleteTrackingContext,
} from '@hc-frontend/features-tracking';
import type { Session } from 'next-auth';
import { SessionProvider } from 'next-auth/react';
import type React from 'react';
import { useEffect } from 'react';

function runInitialization(
  session: ReturnType<typeof useAuthentication>,
  tracker: ReturnType<typeof useCompleteTrackingContext>['tracker'],
) {
  if (typeof globalThis.initializeClientApplicationContext === 'function')
    return;

  const log = getClientLoggerFactory();

  const context: ClientApplicationContext = {
    log,
    getMemberId: getMemberIdClientSide(session),
    getMemberToken: getMemberTokenClientSide(session),
    getDomainUserId: getUserIdClientSide(tracker),
    getDomainSessionId: getSessionIdClientSide(tracker),
    eventBus: createDomEventBus(),
    trackCountMetric: trackCountMetricFactory({
      publish: createDatadogPublisherCount(log),
      log,
    }),
  };

  globalThis.initializeClientApplicationContext = () => context;
}

export function ApplicationContextWithoutSession({
  children,
}: React.PropsWithChildren) {
  const session = useAuthentication();
  const { tracker } = useCompleteTrackingContext();

  runInitialization(session, tracker);

  useEffect(() => {
    // Dump the user id and member id to the cookies
    const context = getClientApplicationContext(
      ApplicationContextWithoutSession,
    );
    context.getDomainUserId();
    context.getDomainSessionId();
  }, []);

  return children;
}

export function ClientApplicationContext({
  children,
  session,
  config,
}: React.PropsWithChildren<{
  session: Session | null;
  config: ClientApplicationConfigOptions;
}>) {
  globalThis.initializeClientApplicationConfig = () => config;

  return (
    <SessionProvider session={session} basePath="/mp2/api/auth">
      <CompleteTrackingProvider
        adapter={{
          type: 'snowplow',
          props: {
            isProd: config.core.appEnvironment === 'prd',
          },
        }}
      >
        <ApplicationContextWithoutSession>
          {children}
        </ApplicationContextWithoutSession>
      </CompleteTrackingProvider>
    </SessionProvider>
  );
}
